import React, { useCallback, useRef, useState } from 'react';
import './Video.scss';

import Lightbox from 'fslightbox-react';

import BackgroundImage from '../../assets/img/video/background.png';
import BackgroundImageAVIF from '../../assets/img/video/background.avif';
import BackgroundImageWEBP from '../../assets/img/video/background.webp';
import TextImage from '../../assets/img/video/text-image.png';
import TextImageAVIF from '../../assets/img/video/text-image.avif';
import TextImageWEBP from '../../assets/img/video/text-image.webp';

import videos, {
  youtubeThumbnail,
  youtubeThumbnailFallback,
  youtubeUrl,
} from './videos.js';

/**
 * Troca a capa do YouTube pela versão de menor resolução quando a
 * `maxresdefault` não existe para aquele vídeo.
 *
 * @param {object} event
 * @param {string} id
 */
const fallbackToLowerResolution = (event, id) => {
  const image = event.target;
  const fallback = youtubeThumbnailFallback(id);

  if (image.src === fallback) return;

  image.src = fallback;
};

const Video = () => {
  /** @var boolean */
  const [videoIsOpen, setVideoOpenState] = useState(false);

  /** @var number */
  const [activeIndex, setActiveIndex] = useState(0);

  /** @var object */
  const trackRef = useRef(null);

  /** @var object */
  const activeVideo = videos[activeIndex];

  /** @var function */
  const scrollThumbnailIntoView = useCallback((index) => {
    const track = trackRef.current;
    const thumbnail = track.children[index];

    if (!thumbnail) return;

    const trackRect = track.getBoundingClientRect();
    const thumbnailRect = thumbnail.getBoundingClientRect();

    // posição absoluta: cliques em sequência não se anulam no meio da animação
    track.scrollTo({
      left:
        track.scrollLeft +
        thumbnailRect.left +
        thumbnailRect.width / 2 -
        (trackRect.left + trackRect.width / 2),
      behavior: 'smooth',
    });
  }, []);

  /** @var function */
  const selectVideo = (index) => {
    setActiveIndex(index);
    scrollThumbnailIntoView(index);
  };

  // render
  return (
    <section className="section-video">
      {/**
        * A `key` remonta o lightbox junto com o vídeo ativo, garantindo que ele
        * abra sempre a fonte selecionada.
        */}
      <Lightbox
        key={activeIndex}
        toggler={videoIsOpen}
        sources={[youtubeUrl(activeVideo.id)]}
      />

      <div className="container">
        <div
          className="background"
          rel="lazy_load"
          data-url={BackgroundImage}
          data-webp={BackgroundImageWEBP}
          data-avif={BackgroundImageAVIF}
          data-distance="0.01"
        ></div>

        <div className="title">
          <div className="top">
            Aproveite a imensidão<br /> das águas para se
          </div>
          <picture>
            <source srcset={TextImageAVIF} type="image/avif" />
            <source srcset={TextImageWEBP} type="image/webp" />
            <img src={TextImage} alt="Aproveite a imensidão das águas para se conectar consigo mesmo" className="el-img"/>
          </picture>
          <div className="bottom">
            Consigo mesmo
          </div>
        </div>

        <div className="video-player">
          <button
            type="button"
            className="play-button"
            aria-label={`Assistir: ${activeVideo.title}`}
            onClick={() => setVideoOpenState(!videoIsOpen)}>
          </button>

          <div className="thumbnail">
            {activeVideo.thumbnail ?
              <picture>
                {activeVideo.thumbnail.avif &&
                  <source srcset={activeVideo.thumbnail.avif} type="image/avif" />
                }
                {activeVideo.thumbnail.webp &&
                  <source srcset={activeVideo.thumbnail.webp} type="image/webp" />
                }
                <img
                  src={activeVideo.thumbnail.src}
                  alt={activeVideo.title}
                />
              </picture>
              :
              <img
                src={youtubeThumbnail(activeVideo.id)}
                alt={activeVideo.title}
                onError={(event) => fallbackToLowerResolution(event, activeVideo.id)}
              />
            }
          </div>
        </div>

        {videos.length > 1 &&
          <div className="thumbnails">
            <button
              type="button"
              className="thumbnails__control thumbnails__control--prev"
              disabled={activeIndex === 0}
              aria-label="Vídeo anterior"
              onClick={() => selectVideo(activeIndex - 1)}>
            </button>

            <div
              className="thumbnails__track"
              ref={trackRef}>
              {videos.map((video, key) => (
                <button
                  key={key}
                  type="button"
                  className={`thumbnails__item ${key === activeIndex ? 'is-active' : ''}`}
                  aria-label={`Selecionar vídeo: ${video.title}`}
                  onClick={() => selectVideo(key)}>
                  <img
                    loading="lazy"
                    src={video.thumbnail ? video.thumbnail.src : youtubeThumbnail(video.id)}
                    alt={video.title}
                    onError={(event) => {
                      if (video.thumbnail) return;
                      fallbackToLowerResolution(event, video.id);
                    }}
                  />
                </button>
              ))}
            </div>

            <button
              type="button"
              className="thumbnails__control thumbnails__control--next"
              disabled={activeIndex === videos.length - 1}
              aria-label="Próximo vídeo"
              onClick={() => selectVideo(activeIndex + 1)}>
            </button>
          </div>
        }
      </div>
    </section>
  );
}

export default Video;
